/**
 * Виды группировки, фильтры и служебные ключи карты данных (макет map.js, map-topology.js).
 */

/* ────────────────────────────────────────────
 * Группировка
 * ──────────────────────────────────────────── */

/**
 * Обзор инфраструктуры, namespace и четыре прежних вида макета (map-topology.js:54-59, спецификация :147): регионы
 * с кластерами, только регионы, только кластеры и типы ресурсов. Группировка меняет
 * отображение, состав исходных ресурсов и связей сохраняется.
 */
export const GROUPING_MODES = [
	'infrastructure',
	'namespace',
	'region-cluster',
	'region',
	'cluster',
	'resource-type',
] as const;
export type GroupingMode = (typeof GROUPING_MODES)[number];

export const DEFAULT_GROUPING: GroupingMode = 'infrastructure';

export const GROUPING_LABEL_KEYS: Record<GroupingMode, string> = {
	infrastructure: 'dataMap:grouping.infrastructure',
	namespace: 'dataMap:grouping.namespace',
	'region-cluster': 'dataMap:grouping.regionCluster',
	region: 'dataMap:grouping.region',
	cluster: 'dataMap:grouping.cluster',
	'resource-type': 'dataMap:grouping.resourceType',
};

/**
 * Другие записи вида в адресе. Значения макета (map.js:77-81: regions-clusters, regions, clusters,
 * types) открывают свои виды: ссылка, собранная по макету, открывает тот же экран. Прежние виды
 * карты раскладывали узлы по регионам и namespace: старый адрес открывает ближайший — по регионам
 */
const LEGACY_GROUPINGS: Readonly<Record<string, GroupingMode>> = {
	'regions-clusters': 'region-cluster',
	regions: 'region',
	clusters: 'cluster',
	types: 'resource-type',
	name: 'region',
	groups: 'region',
};

/** Вид из адреса: запись макета — свой вид, прежний — ближайший, неизвестный — по умолчанию */
export function groupingFrom(value: string | null): GroupingMode {
	if (value === null) return DEFAULT_GROUPING;
	if ((GROUPING_MODES as readonly string[]).includes(value)) return value as GroupingMode;
	return LEGACY_GROUPINGS[value] ?? DEFAULT_GROUPING;
}

/* ────────────────────────────────────────────
 * Служебные ключи
 * ──────────────────────────────────────────── */

/**
 * Служебные ключи групп и фильтров: «регион не указан», «внешний контур» и «без кластера».
 *
 * Значение одновременно попадает в ключи групп и в адрес фильтра, поэтому переводу не
 * подлежит: фильтр, выбранный на одном языке, перестал бы совпадать с ключами, вычисленными
 * на другом. Переводится только отображение — в месте показа. Строки не совпадают ни с одним
 * настоящим регионом или id кластера.
 */
export const UNKNOWN_REGION = '__unknown_region__';
export const EXTERNAL_REGION = '__external__';
export const NO_CLUSTER = '__no_cluster__';

/* ────────────────────────────────────────────
 * Фильтры
 * ──────────────────────────────────────────── */

/** Тип узла макета: сервисы (и внешние) или хранилища */
export type NodeKind = 'service' | 'storage';

/**
 * Фильтры карты из адреса (макет visibleMapNodes): поиск, тип узла, высокий риск, регион и
 * кластер. Регион и кластер — по одному значению, как селекты макета; пустая строка — фильтра нет
 */
export interface MapFilters {
	q: string;
	kind: NodeKind | '';
	highRisk: boolean;
	region: string;
	cluster: string;
}

export const EMPTY_FILTERS: MapFilters = {
	q: '',
	kind: '',
	highRisk: false,
	region: '',
	cluster: '',
};

export function hasActiveFilters(filters: MapFilters): boolean {
	return Boolean(
		filters.q.trim() || filters.kind || filters.highRisk || filters.region || filters.cluster,
	);
}

/* ────────────────────────────────────────────
 * Масштаб и перемещение
 * ──────────────────────────────────────────── */

/**
 * Масштаб и сдвиг сцены (mapView.ts). zoom — от естественного размера сцены (1 — 100 %), x и y —
 * положение начала сцены на холсте в пикселях экрана
 */
export interface MapTransform {
	zoom: number;
	x: number;
	y: number;
}

/** Естественный размер от начала холста: вид, пока размеры сцены и холста не измерены */
export const NATURAL_VIEW: MapTransform = { zoom: 1, x: 0, y: 0 };

/** Пределы и шаг масштаба макета (map.js:316-320): 60–220 % естественного размера, шаг 20 % */
export const MIN_ZOOM = 0.6;
export const MAX_ZOOM = 2.2;
export const ZOOM_STEP = 0.2;
